import { asPartialDef } from "../coerce";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

/** Option of a quiz. */
export interface QuizOption {
  id: string;
  text: string;
}

export interface QuizDef {
  prompt: string;
  options: QuizOption[];
  /** IDs of the correct options. */
  correct: string[];
  /** Multiple selection? If not, a single option. */
  multiple?: boolean;
  /** Partial credit on multiple selection (default true). */
  partialCredit?: boolean;
}

export interface QuizResponse {
  selected: string[];
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

export const quizBlock: BlockPlugin<QuizDef, QuizResponse> = {
  type: "quiz",
  interaction: "select",
  autoGradable: true,

  validate(def: unknown): ValidationResult<QuizDef> {
    const errors: string[] = [];
    const d = asPartialDef<QuizDef>(def);
    if (!d) {
      return { ok: false, errors: ["quiz: definición no es un objeto"] };
    }
    if (typeof d.prompt !== "string" || d.prompt.trim() === "") {
      errors.push("quiz: `prompt` requerido");
    }
    if (!Array.isArray(d.options) || d.options.length < 2) {
      errors.push("quiz: al menos 2 `options`");
    }
    const optionIds = new Set((d.options ?? []).map((o) => o?.id));
    if (!isStringArray(d.correct) || d.correct.length === 0) {
      errors.push("quiz: `correct` requerido");
    } else if (!d.correct.every((c) => optionIds.has(c))) {
      errors.push("quiz: `correct` referencia opciones inexistentes");
    }
    if (!d.multiple && (d.correct?.length ?? 0) !== 1) {
      errors.push("quiz: quiz de opción única requiere exactamente 1 correcta");
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        prompt: d.prompt ?? "",
        options: d.options ?? [],
        correct: d.correct ?? [],
        multiple: d.multiple ?? false,
        partialCredit: d.partialCredit ?? true,
      },
    };
  },

  grade(def: QuizDef, response: QuizResponse | undefined): GradeResult {
    const selected = new Set(response?.selected ?? []);
    const correct = new Set(def.correct);
    const revealed = def.correct;

    if (!def.multiple) {
      const ok = selected.size === 1 && correct.has([...selected][0] ?? "");
      return { correct: ok, score: ok ? 1 : 0, maxScore: 1, revealed };
    }

    // Multiple: hits minus misses, over the number of correct ones (partial credit).
    let hits = 0;
    let wrong = 0;
    for (const s of selected) {
      if (correct.has(s)) {
        hits += 1;
      } else {
        wrong += 1;
      }
    }
    const exact = hits === correct.size && wrong === 0;
    if (!def.partialCredit) {
      return { correct: exact, score: exact ? 1 : 0, maxScore: 1, revealed };
    }
    const raw = (hits - wrong) / correct.size;
    const score = Math.max(0, Math.min(1, raw));
    return { correct: exact, score, maxScore: 1, revealed };
  },
};
